import React, { useState, useEffect, useRef } from 'react';
import './AccommodationForm.css';
import { useLang } from '../i18n';

// Google Apps Script Web App endpoint (doPost = booking, doGet = availability).
const SHEET_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbzKt3eo3rTVSS4FmlimKTu0aIYjjRCczWws0gXYlfMbhz0pQTCulM5eUuEn_2upAfSodw/exec';
// Shared secret — must match SECRET in the Apps Script. Basic bot/abuse deterrent only
// (it ships in the public bundle, so it is not real security).
const SHEET_TOKEN = 'sjy-e85adcb0e9808eabe2bc063d';

// Bump once per edition — used in the header and every tournament subtitle.
const EVENT_YEAR = '2026';

// Header layout variant: 'CD' = minimal centered stack with contextual pill,
// 'D' = solid gradient band with tournament badge + numbered steps with ticks.
const HEADER_VARIANT = 'CD';

const COUNTRIES = [
  { code: 'SK', flag: '🇸🇰', dial: '+421', name: 'Slovakia' },
  { code: 'CZ', flag: '🇨🇿', dial: '+420', name: 'Czech Republic' },
  { code: 'HU', flag: '🇭🇺', dial: '+36', name: 'Hungary' },
  { code: 'AT', flag: '🇦🇹', dial: '+43', name: 'Austria' },
  { code: 'PL', flag: '🇵🇱', dial: '+48', name: 'Poland' },
  { code: 'DE', flag: '🇩🇪', dial: '+49', name: 'Germany' },
  { code: 'GB', flag: '🇬🇧', dial: '+44', name: 'United Kingdom' },
  { code: 'FR', flag: '🇫🇷', dial: '+33', name: 'France' },
  { code: 'IT', flag: '🇮🇹', dial: '+39', name: 'Italy' },
  { code: 'ES', flag: '🇪🇸', dial: '+34', name: 'Spain' },
  { code: 'NL', flag: '🇳🇱', dial: '+31', name: 'Netherlands' },
  { code: 'BE', flag: '🇧🇪', dial: '+32', name: 'Belgium' },
  { code: 'CH', flag: '🇨🇭', dial: '+41', name: 'Switzerland' },
  { code: 'SE', flag: '🇸🇪', dial: '+46', name: 'Sweden' },
  { code: 'NO', flag: '🇳🇴', dial: '+47', name: 'Norway' },
  { code: 'DK', flag: '🇩🇰', dial: '+45', name: 'Denmark' },
  { code: 'FI', flag: '🇫🇮', dial: '+358', name: 'Finland' },
  { code: 'PT', flag: '🇵🇹', dial: '+351', name: 'Portugal' },
  { code: 'RO', flag: '🇷🇴', dial: '+40', name: 'Romania' },
  { code: 'HR', flag: '🇭🇷', dial: '+385', name: 'Croatia' },
  { code: 'RS', flag: '🇷🇸', dial: '+381', name: 'Serbia' },
  { code: 'SI', flag: '🇸🇮', dial: '+386', name: 'Slovenia' },
  { code: 'UA', flag: '🇺🇦', dial: '+380', name: 'Ukraine' },
  { code: 'TR', flag: '🇹🇷', dial: '+90', name: 'Turkey' },
  { code: 'BG', flag: '🇧🇬', dial: '+359', name: 'Bulgaria' },
  { code: 'GR', flag: '🇬🇷', dial: '+30', name: 'Greece' },
  { code: 'US', flag: '🇺🇸', dial: '+1', name: 'United States' },
  { code: 'CA', flag: '🇨🇦', dial: '+1', name: 'Canada' },
  { code: 'AU', flag: '🇦🇺', dial: '+61', name: 'Australia' },
  { code: 'JP', flag: '🇯🇵', dial: '+81', name: 'Japan' },
  { code: 'CN', flag: '🇨🇳', dial: '+86', name: 'China' },
  { code: 'KR', flag: '🇰🇷', dial: '+82', name: 'South Korea' },
  { code: 'IN', flag: '🇮🇳', dial: '+91', name: 'India' },
  { code: 'BR', flag: '🇧🇷', dial: '+55', name: 'Brazil' },
  { code: 'NZ', flag: '🇳🇿', dial: '+64', name: 'New Zealand' },
];

const TOURNAMENTS = [
  { id: 'U13', title: 'Slovak Junior U13', age: '13', badge: 'af-badge-u13' },
  { id: 'U15', title: 'Slovak Junior U15', age: '15', badge: 'af-badge-u15' },
  { id: 'U17', title: 'Slovak Junior U17', age: '17', badge: 'af-badge-u17' },
  // Junior tournament hidden for now — keep for future reuse
  // { id: 'Junior', title: 'Slovak Junior Open', sub: `Junior · Trenčín ${EVENT_YEAR}`, badge: 'af-badge-jr' },
];

// Event dates per tournament — pre-fill (not limit) the room date pickers.
// U15 is "Upcoming" (no fixed dates yet), so its pickers start empty.
const TOURNAMENT_DATES = {
  U13: { start: '2026-09-25', end: '2026-09-27' },
  U17: { start: '2026-09-25', end: '2026-09-27' },
};

// Per-tournament theme: accent drives header/buttons/highlights, bg tints the modal.
// Accents stay in the site's teal family; the two darkest are lifted slightly for legibility.
const THEMES = {
  U13: { accent: '#31969B', bg: '#0b2627' },
  U15: { accent: '#2a7e80', bg: '#0a1f20' },
  U17: { accent: '#267073', bg: '#0b2324' },
  Junior: { accent: '#1f5e60', bg: '#081a1b' },
};
const DEFAULT_THEME = { accent: '#267073', bg: '#0D2526' };

const HOTELS = [
  {
    id: 'arena', name: 'Penzión ARENA', distKey: 'atVenue',
    badgeKey: 'breakfast', capacity: 11,
    roomTypes: [
      { type: 'Double room', price: 85, capacity: 2 },
      { type: 'Triple room', price: 100, capacity: 3 },
      { type: '4-person room', price: 110, capacity: 4 },
    ],
  },
  {
    id: 'pod', name: 'Hotel Pod Hradom ★★★', distKey: 'fromVenue14',
    badgeKey: 'breakfast', capacity: 11,
    roomTypes: [
      { type: 'Double room', price: 95, capacity: 2 },
      { type: 'Triple room', price: 110, capacity: 3 },
    ],
  },
  {
    id: 'gaborik', name: 'Hotel Gáborík ★★★', distKey: 'fromVenue19',
    badgeKey: 'breakfast', capacity: 11,
    roomTypes: [
      { type: 'Single room', price: 65, capacity: 1 },
      { type: 'Double room', price: 95, capacity: 2 },
      { type: 'Triple room', price: 110, capacity: 3 },
    ],
  },
  {
    id: 'city', name: 'City Centre Apts', distKey: 'fromVenue14',
    badgeKey: 'breakfast', capacity: 4, minNights: 3,
    roomTypes: [
      { type: 'Apartment (2-4 persons)', price: 170, capacity: 4 },
    ],
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function nightsBetween(a, b) {
  if (!a || !b) return null;
  const d = (new Date(b) - new Date(a)) / 86400000;
  return d > 0 ? d : null;
}

function addDays(iso, n) {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function AccommodationForm({ onClose }) {
  const { t, tr, lang } = useLang();
  const [step, setStep] = useState(1);
  const [tournament, setTournament] = useState(null);
  const [contact, setContact] = useState({ fname: '', lname: '', phone: '', email: '' });
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState({});      // { [hotelId]: roomsBooked }
  const [availLoading, setAvailLoading] = useState(false);
  const [availUnknown, setAvailUnknown] = useState(false); // fetch failed/timed out
  const phoneRef = useRef(null);
  const modalRef = useRef(null);

  // Lock background scroll while modal is open; Escape closes the country panel
  // first if open, otherwise the modal; Tab cycles focus within the modal.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (dropdownOpen) { setDropdownOpen(false); setCountryFilter(''); }
        else onClose();
      } else if (e.key === 'Enter' && modalRef.current && !dropdownOpen) {
        // Enter confirms the current step. Skipped only when focus is on a real
        // button, or on a pick-card that is NOT selected yet (there Enter selects
        // first; a second Enter on the now-selected card advances).
        const el = document.activeElement;
        const tag = el ? el.tagName : '';
        const isPickCard = el && el.classList && el.classList.contains('af-pick-card');
        const unselectedCard = isPickCard && !el.classList.contains('selected');
        const otherRoleButton = !isPickCard && el && el.getAttribute && el.getAttribute('role') === 'button';
        if (tag !== 'BUTTON' && !unselectedCard && !otherRoleButton) {
          const nextBtn = modalRef.current.querySelector('.af-footer .af-btn-primary');
          if (nextBtn && !nextBtn.disabled) { e.preventDefault(); nextBtn.click(); }
        }
      } else if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll(
          'button, input, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, dropdownOpen]);

  // Close country dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (phoneRef.current && !phoneRef.current.contains(e.target)) setDropdownOpen(false);
    }
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Live availability: ask the backend how many rooms are already booked per hotel
  // (via JSONP, since Apps Script doesn't send CORS headers). Runs when a tournament
  // is chosen, and again on the review step so near-simultaneous bookings surface.
  // If the backend doesn't answer, counts stay optimistic but availUnknown flags it.
  // Kept as a callback so unrelated step changes never cancel an in-flight fetch.
  const fetchAvailability = React.useCallback(() => {
    if (!tournament || !SHEET_WEBHOOK_URL) return undefined;
    let script;
    const cbName = '__afAvail' + Date.now() + '_' + Math.floor(Math.random() * 1e6);
    setAvailLoading(true);
    const fail = () => {
      setAvailUnknown(true);
      setAvailLoading(false);
      delete window[cbName];
      if (script) script.remove();
    };
    const timeout = setTimeout(fail, 6000);
    window[cbName] = (data) => {
      clearTimeout(timeout);
      if (data && data.byHotel) { setBooked(data.byHotel); setAvailUnknown(false); }
      else setAvailUnknown(true);
      setAvailLoading(false);
      delete window[cbName];
      if (script) script.remove();
    };
    script = document.createElement('script');
    script.src = SHEET_WEBHOOK_URL
      + '?action=availability'
      + '&tournament=' + encodeURIComponent(tournament)
      + '&token=' + encodeURIComponent(SHEET_TOKEN)
      + '&callback=' + cbName;
    script.onerror = () => { clearTimeout(timeout); fail(); };
    document.body.appendChild(script);
    return () => { clearTimeout(timeout); delete window[cbName]; if (script) script.remove(); };
  }, [tournament]);

  // Fetch when the tournament changes…
  useEffect(() => fetchAvailability(), [fetchAvailability]);
  // …and re-check when entering the review step.
  useEffect(() => {
    if (step === 6) return fetchAvailability();
  }, [step, fetchAvailability]);

  // Rooms physically left at a hotel = capacity − already booked (live, keyed by id).
  const availableFor = (h) => Math.max(0, h.capacity - (booked[h.id] || 0));

  // Tournament event window (if known). Only used to pre-fill the date pickers when
  // the event is still in the future; if it's past or "Upcoming"/unknown, pickers
  // start empty (so the native calendar opens on the current month). Users can always
  // overwrite to any dates/duration — the window is a suggestion, not a limit.
  const todayStr = new Date().toISOString().slice(0, 10);
  const eventDates = TOURNAMENT_DATES[tournament] || null;
  const prefillDates = eventDates && eventDates.end >= todayStr ? eventDates : null;

  const emailValid = EMAIL_RE.test(contact.email);
  const contactValid = contact.fname.trim() && contact.lname.trim() && contact.phone.trim() && emailValid;
  const roomsLeft = hotel ? availableFor(hotel) - rooms.length : 0;
  const minNightsFor = hotel && hotel.minNights ? hotel.minNights : 1;
  // Each room needs valid dates and at least ONE fully-named guest; the rest are optional.
  const guestsValid = rooms.length > 0 && rooms.every((r) => {
    const n = nightsBetween(r.checkin, r.checkout);
    const named = r.persons.filter((p) => p.first.trim() && p.last.trim());
    return n && n >= minNightsFor && named.length >= 1;
  });

  function addRoom(rt) {
    if (roomsLeft <= 0) return;
    // pre-fill a 3-night stay from the event start for every hotel (editable);
    // only hotels with minNights (apartments) actually enforce a minimum
    const defaultNights = (hotel && hotel.minNights) || 3;
    const checkin = prefillDates ? prefillDates.start : '';
    const checkout = checkin ? addDays(checkin, defaultNights) : '';
    setRooms((prev) => [
      ...prev,
      {
        type: rt.type, price: rt.price, capacity: rt.capacity,
        checkin, checkout,
        persons: Array.from({ length: rt.capacity }, () => ({ first: '', last: '' })),
      },
    ]);
  }

  function removeRoom(i) {
    setRooms((prev) => prev.filter((_, idx) => idx !== i));
  }

  function setRoomField(i, field, value) {
    setRooms((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  }

  function setPerson(i, j, field, value) {
    setRooms((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const persons = r.persons.map((p, pj) => (pj === j ? { ...p, [field]: value } : p));
      return { ...r, persons };
    }));
  }

  function selectHotel(h) {
    if (availableFor(h) <= 0) return;
    if (hotel && hotel.id !== h.id && rooms.length) {
      if (!window.confirm(tr.booking.confirmHotelChange)) return;
      setRooms([]);
    }
    setHotel(h);
  }

  const grandTotal = rooms.reduce((sum, r) => {
    const n = nightsBetween(r.checkin, r.checkout);
    return sum + (n ? n * r.price : 0);
  }, 0);

  async function submitForm() {
    setSubmitting(true);
    const payload = {
      token: SHEET_TOKEN,
      lang,
      tournament,
      hotelId: hotel ? hotel.id : '',
      contact: {
        firstName: contact.fname, lastName: contact.lname,
        phone: `${country.dial} ${contact.phone}`, email: contact.email,
      },
      hotel: hotel ? hotel.name : '',
      rooms: rooms.map((r) => ({
        type: r.type, price: r.price, checkin: r.checkin, checkout: r.checkout,
        nights: nightsBetween(r.checkin, r.checkout),
        persons: r.persons.map((p) => `${p.first} ${p.last}`.trim()).filter((name) => name),
      })),
      total: grandTotal,
      submittedAt: new Date().toISOString(),
    };
    try {
      if (SHEET_WEBHOOK_URL) {
        await fetch(SHEET_WEBHOOK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload),
        });
      }
      setStep(7);
    } catch (err) {
      window.alert(tr.booking.submitError);
    } finally {
      setSubmitting(false);
    }
  }

  const filteredCountries = countryFilter
    ? COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(countryFilter.toLowerCase()) || c.dial.includes(countryFilter))
    : COUNTRIES;

  const stepNums = [1, 2, 3, 4, 5, 6];

  const theme = (tournament && THEMES[tournament]) || DEFAULT_THEME;

  // Header pill: year -> tournament + dates -> tournament + chosen hotel
  const pillText = !tournament
    ? `Trenčín ${EVENT_YEAR}`
    : hotel && step >= 4
      ? `${tournament} · ${hotel.name}`
      : TOURNAMENT_DATES[tournament]
        ? `${tournament} · ${t('home.eventDateShort')}`
        : `${tournament} · Trenčín ${EVENT_YEAR}`;

  // True when the review-step re-fetch shows fewer rooms left than we're booking.
  const overbooked = !!hotel && availableFor(hotel) < rooms.length;

  return (
    <div className="af-overlay" onClick={onClose}>
      <div
        className="af-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Book accommodation"
        style={{ '--af-accent': theme.accent, '--af-bg': theme.bg }}
        onClick={(e) => e.stopPropagation()}
      >
        {HEADER_VARIANT !== 'D' && (
          <button className="af-close af-close--float" onClick={onClose} aria-label="Close">×</button>
        )}
        {HEADER_VARIANT === 'D' && (
          <div className="af-header-d">
            <button className="af-close af-close--float" onClick={onClose} aria-label="Close">×</button>
            <div className="af-header-d-titlerow">
              {tournament && <span className="af-header-d-badge">{tournament}</span>}
              <span className="af-header-d-title">{t('booking.headerTitle')}</span>
            </div>
            <div className="af-header-d-sub">
              Slovak Junior &amp; Youth
              {TOURNAMENT_DATES[tournament] ? ` · ${t('home.eventDateShort')}` : ''} · Trenčín {EVENT_YEAR}
            </div>
          </div>
        )}

        <div className="af-body">
          {HEADER_VARIANT !== 'D' && (
            <div className="af-hd">
              <div className="af-hd-pill">{pillText}</div>
              <div className="af-hd-title">{t('booking.headerTitle')}</div>
              <div className="af-hd-sub">Slovak Junior &amp; Youth</div>
            </div>
          )}

          {step <= 6 && HEADER_VARIANT !== 'D' && (
            <div className="af-steps af-steps--seg">
              {stepNums.map((n) => (
                <div key={n} className={`af-seg ${step === n ? 'on' : ''} ${step > n ? 'done' : ''}`} />
              ))}
            </div>
          )}

          {step <= 6 && HEADER_VARIANT === 'D' && (
            <div className="af-steps af-steps--dots">
              {stepNums.map((n, idx) => (
                <React.Fragment key={n}>
                  <div className={`af-step ${step === n ? 'active' : ''} ${step > n ? 'done' : ''}`}>
                    {step > n ? '✓' : n}
                  </div>
                  {idx < stepNums.length - 1 && <div className={`af-step-line ${step > n ? 'done' : ''}`} />}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* STEP 1 — Tournament */}
          {step === 1 && (
            <>
              <div className="af-section-label">{t('booking.step1Title')}</div>
              <div className="af-cards-grid">
                {TOURNAMENTS.map((tn) => (
                  <div
                    key={tn.id}
                    className={`af-pick-card ${tournament === tn.id ? 'selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={tournament === tn.id}
                    onClick={() => setTournament(tn.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTournament(tn.id); } }}
                  >
                    <div className={`af-pick-badge ${tn.badge}`}>{tn.id}</div>
                    <div className="af-pick-title">{tn.title}</div>
                    <div className="af-pick-sub">{tr.booking.underAge(tn.age)} · Trenčín {EVENT_YEAR}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* STEP 2 — Contact */}
          {step === 2 && (
            <>
              <div className="af-section-label">{t('booking.contactTitle')}</div>
              <div className="af-grid2">
                <div className="af-field">
                  <span className="af-field-label">{t('booking.firstName')} *</span>
                  <input className="af-input" type="text" placeholder="John"
                    value={contact.fname}
                    onChange={(e) => setContact({ ...contact, fname: e.target.value })} />
                </div>
                <div className="af-field">
                  <span className="af-field-label">{t('booking.lastName')} *</span>
                  <input className="af-input" type="text" placeholder="Smith"
                    value={contact.lname}
                    onChange={(e) => setContact({ ...contact, lname: e.target.value })} />
                </div>
              </div>
              <div className="af-field">
                <span className="af-field-label">{t('booking.phone')} *</span>
                <div className="af-phone" ref={phoneRef}>
                  <div className="af-country-trigger" onClick={() => setDropdownOpen((o) => !o)}>
                    <span className="af-flag">{country.flag}</span>
                    <span className="af-dial">{country.dial}</span>
                    <i className="fas fa-chevron-down af-chevron" aria-hidden="true"></i>
                  </div>
                  <input className="af-input" type="tel" placeholder="900 123 456"
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                </div>
                {dropdownOpen && (
                  <div className="af-country-overlay" onClick={() => { setDropdownOpen(false); setCountryFilter(''); }}>
                    <div className="af-country-panel" onClick={(e) => e.stopPropagation()}>
                      <div className="af-country-panel-head">
                        <span>{t('booking.selectCountry')}</span>
                        <button type="button" aria-label="Close"
                          onClick={() => { setDropdownOpen(false); setCountryFilter(''); }}>×</button>
                      </div>
                      <div className="af-dropdown-search">
                        <input type="text" autoFocus placeholder={t('booking.searchCountry')} value={countryFilter}
                          onChange={(e) => setCountryFilter(e.target.value)} />
                      </div>
                      <div className="af-country-list">
                        {filteredCountries.map((c) => (
                          <div key={c.code} className="af-country-opt"
                            onClick={() => { setCountry(c); setDropdownOpen(false); setCountryFilter(''); }}>
                            <span className="af-flag">{c.flag}</span>
                            <span className="af-cname">{c.name}</span>
                            <span className="af-cdial">{c.dial}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="af-field">
                <span className="af-field-label">{t('booking.email')} *</span>
                <input
                  className={`af-input ${contact.email ? (emailValid ? 'valid' : 'invalid') : ''}`}
                  type="email" placeholder="coach@club.com"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                {contact.email && !emailValid && (
                  <span className="af-error">{t('booking.emailInvalid')}</span>
                )}
              </div>
            </>
          )}

          {/* STEP 3 — Hotel */}
          {step === 3 && (
            <>
              <div className="af-section-label">
                {t('booking.chooseHotel')}{availLoading ? ' · ' + t('booking.checkingAvail') : ''}
              </div>
              {availUnknown && (
                <div className="af-rooms-left low" style={{ marginTop: 0, marginBottom: 12 }}>
                  <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                  {t('booking.availUnknown')}
                </div>
              )}
              <div className="af-cards-grid">
                {HOTELS.map((h) => {
                  const sold = availableFor(h) <= 0;
                  return (
                    <div key={h.id}
                      className={`af-pick-card ${sold ? 'disabled' : ''} ${hotel && hotel.id === h.id ? 'selected' : ''}`}
                      role="button"
                      tabIndex={sold ? -1 : 0}
                      aria-pressed={!!hotel && hotel.id === h.id}
                      aria-disabled={sold}
                      onClick={() => selectHotel(h)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectHotel(h); } }}>
                      <div className="af-pick-title">{h.name}</div>
                      <div className="af-pick-sub">{t('booking.' + h.distKey)}</div>
                      {sold
                        ? <div className="af-hotel-unavail"><i className="fas fa-times-circle" aria-hidden="true"></i> {t('booking.unavailable')}</div>
                        : <div className="af-hotel-badge">{t('booking.' + h.badgeKey)}</div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* STEP 4 — Rooms */}
          {step === 4 && hotel && (
            <>
              <div className="af-section-label">{t('booking.rooms')} — {hotel.name}</div>
              {hotel.roomTypes.map((rt) => (
                <div key={rt.type} className="af-room-row">
                  <div className="af-room-info">
                    <div className="af-room-type">{tr.booking.roomTypes[rt.type] || rt.type}</div>
                    <div className="af-room-price">{rt.price} {t('booking.perNight')}</div>
                  </div>
                  <button className="af-add-btn" disabled={roomsLeft <= 0} onClick={() => addRoom(rt)}>
                    {roomsLeft <= 0 ? t('booking.soldOut') : t('booking.addRoom')}
                  </button>
                </div>
              ))}
              <div className={`af-rooms-left ${roomsLeft <= 0 ? 'none' : roomsLeft <= 2 ? 'low' : 'ok'}`}>
                <i className={`fas ${roomsLeft <= 0 ? 'fa-times-circle' : roomsLeft <= 2 ? 'fa-exclamation-circle' : 'fa-check-circle'}`} aria-hidden="true"></i>
                {roomsLeft <= 0
                  ? t('booking.noRoomsLeft')
                  : roomsLeft <= 2
                    ? tr.booking.onlyRoomsLeft(roomsLeft)
                    : tr.booking.roomsAvailable(roomsLeft, hotel.capacity)}
              </div>
              {rooms.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div className="af-section-label">{t('booking.selectedRooms')} ({rooms.length})</div>
                  {rooms.map((r, i) => (
                    <div key={i} className="af-res-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13 }}><i className="fas fa-door-open" style={{ marginRight: 6 }} aria-hidden="true"></i>{tr.booking.roomTypes[r.type] || r.type} — {r.price} {t('booking.perNight')}</span>
                      <button className="af-remove-btn" onClick={() => removeRoom(i)}>{t('booking.remove')}</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* STEP 5 — Guests + dates */}
          {step === 5 && (
            <>
              {rooms.map((r, i) => {
                const nights = nightsBetween(r.checkin, r.checkout);
                const sub = nights ? nights * r.price : null;
                return (
                  <div key={i} className="af-res-item">
                    <div className="af-res-head">
                      <div className="af-res-title"><i className="fas fa-door-open" style={{ marginRight: 6 }} aria-hidden="true"></i>{tr.booking.roomTypes[r.type] || r.type} — {r.price} {t('booking.perNight')}</div>
                      <button className="af-remove-btn" onClick={() => removeRoom(i)}>{t('booking.remove')}</button>
                    </div>
                    <div className="af-date-row">
                      <div className="af-field" style={{ margin: 0 }}>
                        <span className="af-field-label" style={{ fontSize: 12 }}>{t('booking.checkin')}</span>
                        <input className="af-input" type="date" value={r.checkin}
                          onChange={(e) => setRoomField(i, 'checkin', e.target.value)} />
                      </div>
                      <div className="af-field" style={{ margin: 0 }}>
                        <span className="af-field-label" style={{ fontSize: 12 }}>{t('booking.checkout')}</span>
                        <input className="af-input" type="date" value={r.checkout}
                          onChange={(e) => setRoomField(i, 'checkout', e.target.value)} />
                      </div>
                    </div>
                    {nights && (
                      <div className="af-nights"><i className="fas fa-moon" style={{ marginRight: 4 }} aria-hidden="true"></i>{tr.booking.nights(nights)} · {sub} €</div>
                    )}
                    {nights && nights < minNightsFor && (
                      <div className="af-error" style={{ marginBottom: 8 }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: 4 }} aria-hidden="true"></i>
                        {t('booking.minNights3')}
                      </div>
                    )}
                    {r.persons.map((p, j) => (
                      <div key={j} className="af-person-row">
                        <input className="af-input" type="text" placeholder={tr.booking.personFirst(j + 1)}
                          value={p.first} onChange={(e) => setPerson(i, j, 'first', e.target.value)} />
                        <input className="af-input" type="text" placeholder={tr.booking.personLast(j + 1)}
                          value={p.last} onChange={(e) => setPerson(i, j, 'last', e.target.value)} />
                      </div>
                    ))}
                  </div>
                );
              })}
              <button className="af-add-room" onClick={() => setStep(4)}>
                <i className="fas fa-plus" aria-hidden="true"></i> {t('booking.addAnotherRoom')}
              </button>
            </>
          )}

          {/* STEP 6 — Summary */}
          {step === 6 && (
            <>
              <div className="af-section-label">{t('booking.summaryTitle')}</div>
              <div className="af-summary-contact">
                <strong>{t('booking.tournamentLabel')}</strong> {tournament}<br />
                <strong>{t('booking.hotelLabel')}</strong> {hotel ? hotel.name : '-'}<br />
                <strong>{t('booking.contactLabel')}</strong> {contact.fname} {contact.lname}<br />
                <strong>{t('booking.phoneLabel')}</strong> {country.flag} {country.dial} {contact.phone}<br />
                <strong>{t('booking.emailLabel')}</strong> {contact.email}
              </div>
              {rooms.map((r, i) => {
                const nights = nightsBetween(r.checkin, r.checkout);
                const sub = nights ? nights * r.price : null;
                return (
                  <div key={i} className="af-summary-room">
                    <div className="af-summary-room-head">
                      <span>{tr.booking.roomTypes[r.type] || r.type}</span>
                      <span>{sub ? `${sub} €` : '—'}</span>
                    </div>
                    <div className="af-summary-dates">
                      <i className="fas fa-calendar" style={{ marginRight: 4 }} aria-hidden="true"></i>
                      {r.checkin && r.checkout
                        ? `${r.checkin} → ${r.checkout}${nights ? ` (${tr.booking.nights(nights)})` : ''}`
                        : '-'}
                    </div>
                    {r.persons.map((p, j) => (
                      <div key={j} className="af-summary-person">
                        <i className="fas fa-user" style={{ marginRight: 4 }} aria-hidden="true"></i>
                        {t('booking.person')} {j + 1}: {(p.first || p.last) ? `${p.first} ${p.last}`.trim() : '—'}
                      </div>
                    ))}
                  </div>
                );
              })}
              {overbooked && (
                <div className="af-rooms-left none" style={{ marginBottom: 12 }}>
                  <i className="fas fa-times-circle" aria-hidden="true"></i>
                  {availableFor(hotel)
                    ? tr.booking.overSome(availableFor(hotel), hotel.name, rooms.length - availableFor(hotel))
                    : tr.booking.overFull(hotel.name)}
                </div>
              )}
              <div className="af-total-bar">
                <span style={{ fontSize: 14 }}>{t('booking.estimatedTotal')}</span>
                <span className="af-total-amount">{grandTotal ? `${grandTotal} €` : '—'}</span>
              </div>
              <div className="af-note">
                {t('booking.paymentNote')}
              </div>
            </>
          )}

          {/* STEP 7 — Success */}
          {step === 7 && (
            <div className="af-success">
              <div className="af-success-icon"><i className="fas fa-check" aria-hidden="true"></i></div>
              <div className="af-success-title">{t('booking.successTitle')}</div>
              <div className="af-success-text">
                {t('booking.successText')}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step <= 6 && (
          <div className="af-footer">
            {step > 1
              ? <button className="af-btn-outline" onClick={() => setStep(step === 5 ? 4 : step - 1)}><i className="fas fa-arrow-left" aria-hidden="true"></i> {t('booking.btnBack')}</button>
              : <span />}
            {step === 1 && <button className="af-btn-primary" disabled={!tournament} onClick={() => setStep(2)}>{t('booking.btnContact')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 2 && <button className="af-btn-primary" disabled={!contactValid} onClick={() => setStep(3)}>{t('booking.btnHotel')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 3 && <button className="af-btn-primary" disabled={!hotel} onClick={() => setStep(4)}>{t('booking.btnRoom')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 4 && <button className="af-btn-primary" disabled={rooms.length === 0} onClick={() => setStep(5)}>{t('booking.btnGuests')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 5 && <button className="af-btn-primary" disabled={!guestsValid} onClick={() => setStep(6)}>{t('booking.btnReview')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 6 && <button className="af-btn-primary" disabled={submitting || overbooked} onClick={submitForm}>{submitting ? <><span className="af-spinner" />{t('booking.btnSubmitting')}</> : <>{t('booking.btnSubmit')} <i className="fas fa-paper-plane" aria-hidden="true"></i></>}</button>}
          </div>
        )}

        {step === 7 && (
          <div className="af-footer" style={{ justifyContent: 'flex-end' }}>
            <button className="af-btn-primary" onClick={onClose}>{t('booking.btnClose')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccommodationForm;
